import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://coder-api.finhub.workers.dev';
// Convert preset to hours for API
export function presetToHours(preset) {
    const map = {
        '1h': 1,
        '6h': 6,
        '24h': 24,
        '7d': 7 * 24,
        '30d': 30 * 24,
        'custom': 24,
    };
    return map[preset];
}
// Get auth token
const getAuthToken = () => {
    return localStorage.getItem('auth_token') || '';
};
// Create axios instance with auth
const api = axios.create({
    baseURL: API_BASE_URL,
});
api.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});
export function useDashboard() {
    const [dateRange, setDateRange] = useState({ preset: '24h' });
    const [state, setState] = useState({
        metrics: [],
        workers: [],
        tasks: [],
        alerts: [],
        cost: null,
        performance: null,
        recommendations: [],
        queries: [],
        isConnected: false,
        isLoading: false,
        error: null,
    });
    const hours = useMemo(() => {
        if (dateRange.preset === 'custom' && dateRange.start && dateRange.end) {
            return Math.max(1, Math.round((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60)));
        }
        return presetToHours(dateRange.preset);
    }, [dateRange]);
    // Fetch dashboard data using Promise.allSettled for resilience
    const fetchDashboardData = useCallback(async () => {
        setState(prev => ({ ...prev, isLoading: true, error: null }));
        try {
            const results = await Promise.allSettled([
                api.get('/api/dashboard/summary'), // 0: system metrics
                api.get('/api/dashboard/workers'), // 1: worker pool
                api.get(`/api/dashboard/alerts?limit=20`), // 2: alerts
                api.get('/api/dashboard/costs'), // 3: cost data
                api.get('/api/performance/summary'), // 4: performance
                api.get('/api/performance/recommendations'), // 5: recommendations
                api.get(`/api/performance/queries?hours=${hours}`), // 6: query stats
            ]);
            const settled = (i, fallback) => {
                const r = results[i];
                return r.status === 'fulfilled' ? r.value.data : fallback;
            };
            // Map /api/dashboard/summary response to time series format for MetricsPanel
            const summaryData = settled(0, {});
            const metricsFromSummary = summaryData.activeWorkers != null
                ? [{ timestamp: new Date().toISOString(), value: summaryData.activeWorkers, type: 'active_workers' }]
                : [];
            // Map /api/dashboard/workers to WorkerMetric[] (aggregate by agentType)
            const rawWorkers = settled(1, { workers: [] }).workers || [];
            const workersByType = new Map();
            for (const w of rawWorkers) {
                const entry = workersByType.get(w.agentType) || { total: 0, active: 0, idle: 0 };
                entry.total++;
                if (w.status === 'busy')
                    entry.active++;
                else
                    entry.idle++;
                workersByType.set(w.agentType, entry);
            }
            const workerMetrics = Array.from(workersByType.entries()).map(([agentType, counts]) => ({ agentType, ...counts }));
            // Map /api/dashboard/alerts to AlertHistory[]
            const rawAlerts = settled(2, { alerts: [] }).alerts || [];
            const mappedAlerts = rawAlerts.map(a => ({
                id: a.id,
                projectId: a.projectId || '',
                alertType: a.type || 'unknown',
                severity: a.severity || 'info',
                message: a.message || '',
                status: a.resolvedAt ? 'resolved' : a.readAt ? 'acknowledged' : 'triggered',
                timestamp: a.createdAt || new Date().toISOString(),
            }));
            // Map /api/dashboard/costs to CostSnapshot
            const rawCosts = settled(3, { costs: [] }).costs || [];
            const totalCostCents = rawCosts.reduce((sum, c) => sum + (c.totalCost || 0), 0);
            const costSnapshot = rawCosts.length > 0
                ? { totalCents: Math.round(totalCostCents * 100), budgetUtilization: 0 }
                : null;
            setState(prev => ({
                ...prev,
                metrics: metricsFromSummary,
                workers: workerMetrics,
                tasks: [],
                alerts: mappedAlerts,
                cost: costSnapshot,
                performance: settled(4, null),
                recommendations: settled(5, { recommendations: [] }).recommendations || [],
                queries: settled(6, { stats: [] }).stats || [],
                isLoading: false,
                error: null,
            }));
        }
        catch (err) {
            console.error('Failed to fetch dashboard data:', err);
            setState(prev => ({
                ...prev,
                error: err.message || 'Failed to load dashboard',
                isLoading: false
            }));
        }
    }, [hours]);
    // SSE connection for real-time updates
    useEffect(() => {
        const connectEventSource = () => {
            const eventSource = new EventSource(`${API_BASE_URL}/api/stream/events?projectId=dashboard`);
            eventSource.onopen = () => {
                console.log('EventSource connected');
                setState(prev => ({ ...prev, isConnected: true, error: null }));
            };
            eventSource.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    setState(prev => {
                        if (data.type === 'metric') {
                            return { ...prev, metrics: [...prev.metrics, data] };
                        }
                        if (data.type === 'worker_status') {
                            return { ...prev, workers: data.workers };
                        }
                        if (data.type === 'alert') {
                            return { ...prev, alerts: [data.alert, ...prev.alerts].slice(0, 20) };
                        }
                        if (data.type === 'cost') {
                            return { ...prev, cost: data.cost };
                        }
                        if (data.type === 'error') {
                            return { ...prev, error: data.error, isLoading: false };
                        }
                        return prev;
                    });
                }
                catch (err) {
                    console.error('EventSource message error:', err);
                }
            };
            eventSource.onerror = () => {
                // SSE endpoint may be stubbed — don't set blocking error, just mark disconnected
                console.warn('EventSource disconnected (SSE endpoint may not be available)');
                eventSource.close();
                setState(prev => ({ ...prev, isConnected: false }));
            };
            return () => {
                eventSource.close();
            };
        };
        const cleanup = connectEventSource();
        return cleanup;
    }, []);
    // Auto-refresh when date range changes
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);
    return {
        // State
        ...state,
        // Filters
        dateRange,
        setDateRange,
        // Actions
        refresh: fetchDashboardData,
        dismissError: () => setState(prev => ({ ...prev, error: null })),
    };
}
